/**
 * The public shape of a shared link — one definition, three consumers.
 *
 * A share has to work for people who do NOT have the app; that is the whole
 * point of sharing. A custom scheme (`bibleshield://…`) opens nothing on their
 * phone, so the links are ordinary HTTPS URLs on our own domain, claimed as
 * Universal Links (iOS) / App Links (Android). Which means the SAME string is
 * read by three different places:
 *
 *   - `apps/mobile` builds it (the ⋯ → share action) and parses it back on the
 *     way in (the deep-link router),
 *   - `apps/web` serves the association files that list these paths, and
 *     renders the fallback page for someone without the app,
 *   - and the association files themselves have to agree with both.
 *
 * Three copies of a URL grammar is three chances to drift, and a drifted path
 * fails SILENTLY: iOS just opens Safari instead of the app, with nothing to
 * indicate why. So it lives here, with no dependencies, testable under plain
 * `node --test`.
 *
 * Paths are DELIBERATELY spelled out (`/post/…`, `/request/…`) rather than
 * shortened to `/p/` and `/r/`. A shared link is read by a human before it is
 * tapped, and "bibleshield.app/request/…" says what it is.
 *
 * What follows the path, on the other hand, is deliberately meaningless: a
 * SHARE CODE, not the row's id. See `shareCodeFromBytes`.
 */

/** Where shared links live. Must match `apps/web`'s custom domain. */
export const SHARE_ORIGIN = "https://bibleshield.app";

/** What a link can point at. Mirrors the community detail screens. */
export type ShareTargetKind = "post" | "request";

/** A resolved share link. */
export interface ShareTarget {
  kind: ShareTargetKind;
  /**
   * The PUBLIC handle of the thing shared — a `shareCode` (see below), never the
   * internal row id.
   *
   * Links handed out before codes existed carry a raw uuid here instead, and
   * those links must keep working: they are in people's message threads and
   * nothing can be edited out of a WhatsApp history. So this stays a plain
   * opaque string and the server accepts either, rather than the grammar
   * refusing a link it used to produce.
   */
  code: string;
}

/** URL path segment per kind — the single source the AASA paths are built from. */
const SEGMENT: Record<ShareTargetKind, string> = {
  post: "post",
  request: "request",
};

/**
 * The path patterns the association files must list, e.g. `/post/*`.
 *
 * Exported so `apple-app-site-association` is generated from the same table the
 * links are built from, instead of being a hand-kept second copy that can be
 * right on the day it is written and wrong a release later.
 */
export const SHARE_PATH_PATTERNS: string[] = Object.values(SEGMENT).map(
  (segment) => `/${segment}/*`,
);

/**
 * Codes are opaque server-generated strings. This is a shape check, not a
 * validation: it exists so a stray path (`/post/`, `/post/../etc`) can't be read
 * as a code and sent to the API. The server is the authority on whether it
 * resolves to anything.
 *
 * Wide enough to still admit the 36-char uuids older links carry.
 */
const CODE_RE = /^[A-Za-z0-9_-]{1,64}$/;

// ─── The public code ─────────────────────────────────────────────────────────
/**
 * A shared link used to end in the row's own id:
 *
 *   https://bibleshield.app/post/59672d12-7f5e-4fe7-9e04-0a97d146a6f6
 *
 * which publishes an internal primary key to every group chat a post is ever
 * forwarded through. `shareCode` is a second, PUBLIC handle stored beside the
 * id — random, meaningless, and the only one that ever leaves the server. Same
 * move as `Device.publicId` (migration 0019), for the same reason: the id a
 * record is addressed by internally and the string the outside world holds
 * should not be the same string.
 *
 * It buys secrecy of the id, not access control. Anyone with the LINK can still
 * open what it points at — that is what sharing means, and the web fallback
 * shows no content to someone without the app regardless.
 */

/**
 * base64url's alphabet. 64 symbols → exactly 6 bits per character, so three
 * random bytes become four characters with no remainder and no rejection
 * sampling: every code is a uniform draw over the whole space. (A base-62
 * alphabet reads marginally nicer and would need a modulo that quietly biases
 * the first two symbols.)
 */
const CODE_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

/** Bytes of randomness behind one code. 72 bits — far past any birthday
 *  collision at community scale, and the unique index is the backstop. */
export const SHARE_CODE_BYTES = 9;

/** Characters in a code: 9 bytes × 8 bits ÷ 6 bits. */
export const SHARE_CODE_LENGTH = 12;

/**
 * Encode randomness as a share code.
 *
 * Takes the bytes rather than drawing them, for two reasons: this package is
 * built with no DOM and no node types, so it has no `crypto` to call; and a pure
 * function is one a test can pin exactly instead of asserting statistics. The
 * caller (the worker) has WebCrypto — see `newShareCode` in the community
 * router.
 */
export function shareCodeFromBytes(bytes: Uint8Array): string {
  if (bytes.length < SHARE_CODE_BYTES)
    throw new Error(`a share code needs ${SHARE_CODE_BYTES} bytes`);

  let code = "";
  for (let i = 0; i < SHARE_CODE_BYTES; i += 3) {
    const triple =
      ((bytes[i] ?? 0) << 16) |
      ((bytes[i + 1] ?? 0) << 8) |
      (bytes[i + 2] ?? 0);
    for (let shift = 18; shift >= 0; shift -= 6)
      code += CODE_ALPHABET.charAt((triple >> shift) & 63);
  }
  return code;
}

// ─── Campaign parameters ─────────────────────────────────────────────────────
/**
 * Sharing is the cheapest growth loop this app has, and the question it has to
 * answer is which surface's shares actually bring somebody back. The sending
 * side is already measured (`community_share_opened`); without something in the
 * link, the arriving half can't be joined to it — a tap on a shared link looks
 * exactly like any other cold launch.
 *
 * So the link carries the surface that produced it, in the parameter names an
 * off-the-shelf analytics tool already understands. `utm_source` is the only one
 * that varies; the other two exist so a future web-side tool buckets these
 * arrivals as referrals rather than as direct traffic.
 *
 * They are read back by the app itself (`parseShareLink` → the deep-link
 * router), not by the web page, which stays a static fallback.
 */
const SHARE_MEDIUM = "share_link";

/** Which surface produced the link, when the caller doesn't say. */
export const DEFAULT_SHARE_SOURCE = "app_share_sheet";

/**
 * A source we'd have written ourselves. Anything else in an incoming link is
 * dropped rather than forwarded to analytics: the query string is attacker-
 * controlled, and an unbounded string there is unbounded event cardinality.
 */
const SOURCE_RE = /^[a-z0-9_]{1,32}$/;

/** Options for the link a share sheet hands out. */
export interface ShareLinkOptions {
  /** The surface the share was started from, e.g. `post_detail`. Lowercase
   *  snake_case, ≤32 chars; anything else falls back to the default. */
  source?: string;
}

/**
 * The source a link will actually carry: the caller's, when it is one we'd have
 * written ourselves, and the default otherwise.
 *
 * Exported so the SENDING event can record the same string the link carries.
 * The sheet used to log the surface it asked for while the URL quietly fell back
 * to the default — one typo and the two halves of the loop are labelled
 * differently and can never be joined, which is the entire point of measuring
 * them (GEN-12).
 */
export function resolveShareSource(source?: string): string {
  return source && SOURCE_RE.test(source) ? source : DEFAULT_SHARE_SOURCE;
}

/** The canonical https link for a target — what the share sheet hands out.
 *  Named `shareLinkUrl`, not `shareUrl`: the mobile app already has a `shareUrl()`
 *  that means "a link to the app itself" (constants/links.ts), and two functions
 *  with one name and different meanings is a bug waiting for a tired afternoon. */
export function shareLinkUrl(
  target: ShareTarget,
  options: ShareLinkOptions = {},
): string {
  const source = resolveShareSource(options.source);
  const query = `utm_source=${source}&utm_medium=${SHARE_MEDIUM}&utm_campaign=community_${target.kind}`;
  return `${SHARE_ORIGIN}/${SEGMENT[target.kind]}/${target.code}?${query}`;
}

/** A share link as it arrived. */
export interface ParsedShareLink extends ShareTarget {
  /** `utm_source` — the surface that produced the link, when it carries one
   *  shaped like ours. Null for links made before campaign parameters existed,
   *  and for anything a stranger appended. */
  source: string | null;
}

/**
 * Read a target back out of an incoming URL, or `null` when it isn't one of
 * ours.
 *
 * Accepts three forms on purpose:
 *   - `https://bibleshield.app/post/<code>` — the real Universal / App Link,
 *   - `http://…` — a mistyped or downgraded link is still unambiguously ours,
 *   - `bibleshield://post/<code>` — the custom scheme, so a link can be tested on
 *     a simulator (`xcrun simctl openurl`) without the association files being
 *     live yet, and so a build predating verification still lands somewhere.
 *
 * A query string never decides whether the link opens — only what we log about
 * it. Links are forwarded through apps that append their own parameters, and a
 * share that stopped opening because a messenger added a tracking tag would be a
 * miserable bug to find.
 */
export function parseShareLink(
  url: string | null | undefined,
): ParsedShareLink | null {
  if (!url) return null;

  let path: string;
  const lower = url.toLowerCase();
  if (lower.startsWith("bibleshield://")) {
    // A custom-scheme URL's "host" is the first segment, so the whole remainder
    // is the path. Parsing it with `new URL` would put "post" in `hostname` and
    // lower-case it, which is a difference that would bite the day a segment is
    // not already lower case.
    path = url.slice("bibleshield://".length);
  } else if (lower.startsWith("https://") || lower.startsWith("http://")) {
    const afterScheme = url.slice(url.indexOf("://") + 3);
    const slash = afterScheme.indexOf("/");
    if (slash === -1) return null;
    const host = afterScheme.slice(0, slash).toLowerCase();
    // Only our own domain. Someone else's `/post/<code>` is not our link.
    if (host !== "bibleshield.app" && host !== "www.bibleshield.app")
      return null;
    path = afterScheme.slice(slash + 1);
  } else {
    return null;
  }

  // Fragment first, then query: `?a=1#top` and `#top?a=1` both appear in the
  // wild, and splitting on either character at once would put half of one into
  // the other.
  const [beforeHash = ""] = path.split("#");
  const [pathname = "", query = ""] = beforeHash.split("?");

  const [segment, code] = pathname.split("/");
  if (!segment || !code || !CODE_RE.test(code)) return null;

  const kind = (Object.keys(SEGMENT) as ShareTargetKind[]).find(
    (k) => SEGMENT[k] === segment.toLowerCase(),
  );
  if (!kind) return null;

  return { kind, code, source: shareLinkSource(query) };
}

/**
 * The surface a link was made from, read off a bare query string — what
 * `location.search` holds.
 *
 * Exists for the WEB half of the loop (GEN-12). A browser reaching
 * `/post/<code>` already knows the path from its route and has no use for
 * `parseShareLink`; what it does not otherwise know is which share surface sent
 * the visitor, and that visitor is the one who matters most — an installed app
 * intercepts the URL before a browser ever sees it, so everyone who lands on the
 * page is somebody who does NOT have the app yet.
 *
 * Same sanitising as the parser above, and the same reason: the query string is
 * attacker-controlled, and an unbounded string forwarded to analytics is
 * unbounded event cardinality. A leading `?` is accepted because that is how
 * `location.search` arrives.
 */
export function shareLinkSource(
  search: string | null | undefined,
): string | null {
  if (!search) return null;
  const query = search.startsWith("?") ? search.slice(1) : search;
  const source = queryParam(query, "utm_source");
  return source && SOURCE_RE.test(source) ? source : null;
}

/** One parameter out of a raw query string. Hand-rolled rather than
 *  `URLSearchParams`, which this module can neither import nor assume: it has to
 *  stay dependency-free for `node --test` and runs on Hermes, where the global
 *  is a partial polyfill. */
function queryParam(query: string, key: string): string | null {
  for (const pair of query.split("&")) {
    const eq = pair.indexOf("=");
    if (eq === -1 || pair.slice(0, eq) !== key) continue;
    try {
      return decodeURIComponent(pair.slice(eq + 1));
    } catch {
      // A malformed escape (`%zz`) throws. The link still opens; we just don't
      // know where it came from.
      return null;
    }
  }
  return null;
}
